import React from 'react';
import { useNavigate } from 'react-router';

import AddProductForm from '~/components/products/product-detail/add-product-form';
import Modal from '~/components/modals/modal';
import ProductBadge from '~/components/products-grid/products-cell-badge';
import BlockContent from '~/components/layouts/block-content';
import Button from '~/components/button';
import ProductCarousels from '~/components/products/product-detail/product-carousels';
import { useMediaQuery } from '~/hooks/use-media-query';
import getChannelAndPrice from '~/utils/get-channel-price';
import Price from '~/components/modals/product-details/price';
import { sundayStoreProductDetailsRoute } from '~/routes';
import { REGISTRATION_SOURCES } from '~/components/modals/funnel-auth/register-modal-form';
import { productContentSerializers } from '~/components/modals/product-details/product-content-serializers';

import styles from '~/components/modals/product-details/product-details-modal.module.scss';
import modalStyles from '~/components/modals/modal.module.scss';
import carouselStyles from '~/components/products/product-detail/product-carousel.module.scss';

const ProductDetailModal = ({
  channel,
  isOpen,
  objectID,
  onClose,
  position,
  product,
  qtySelected,
  queryID,
  showViewDetailsLink,
  ctaButton,
  showAtc = true,
  showPspCopy,
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (!product) {
    return null;
  }

  const { productDetails, purchaseSku } = product;

  const images = [];
  if (productDetails?.primaryImage) {
    images.push(productDetails.primaryImage);
  }
  if (productDetails?.carouselImages) {
    images.push(...productDetails.carouselImages);
  }

  const { unitPrice, fullPrice } = getChannelAndPrice(channel, product);

  // 'showPspCopy' is used to determine if we should show the PSP copy or the PDP copy
  const descriptionCopy =
    showPspCopy && productDetails?.pspDetailsModalCopy
      ? productDetails?.pspDetailsModalCopy
      : productDetails?.pdpFlexContent || productDetails?.shortDescription;

  return (
    <Modal
      className={modalStyles.productDetail}
      onClose={onClose}
      isOpen={isOpen}
    >
      <div className={styles.container}>
        {isDesktop ? (
          <section className={styles.products}>
            <div className={carouselStyles.productModal}>
              <ProductBadge
                fullPrice={fullPrice}
                productDetails={productDetails}
                purchaseSku={purchaseSku}
                unitPrice={unitPrice}
              />
              <ProductCarousels
                images={images}
                videos={productDetails.videos}
              />
            </div>

            <div className={styles.detailsColumn}>
              <h2 className={styles.name}>{productDetails?.name}</h2>
              <Price
                fullPrice={fullPrice}
                inStock={productDetails?.inStock}
                unitPrice={unitPrice}
              />
              <p className={styles.formFactor}>{productDetails?.formFactor}</p>

              <div className={styles.productDescription}>
                <BlockContent
                  content={descriptionCopy}
                  serializers={productContentSerializers}
                />
              </div>

              {showAtc && (
                <div className={styles.addToCart}>
                  {ctaButton ? (
                    ctaButton
                  ) : (
                    <AddProductForm
                      channel={channel}
                      objectID={objectID}
                      queryID={queryID}
                      position={position}
                      productDetails={productDetails}
                      purchaseSku={purchaseSku}
                      qtySelected={qtySelected}
                      error={!purchaseSku}
                      source={REGISTRATION_SOURCES.LAWN_FUNNEL}
                    />
                  )}
                </div>
              )}

              {showViewDetailsLink && (
                <ViewDetailsLink
                  onClose={onClose}
                  productDetails={productDetails}
                />
              )}
            </div>
          </section>
        ) : (
          <MobileView
            channel={channel}
            fullPrice={fullPrice}
            images={images}
            onClose={onClose}
            productDetails={productDetails}
            purchaseSku={purchaseSku}
            qtySelected={qtySelected}
            unitPrice={unitPrice}
            ctaButton={ctaButton}
            showAtc={showAtc}
            showPspCopy={showPspCopy}
          />
        )}
      </div>
    </Modal>
  );
};

export default ProductDetailModal;

const MobileView = ({
  channel,
  fullPrice,
  images,
  onClose,
  productDetails,
  purchaseSku,
  qtySelected,
  showViewDetailsLink,
  unitPrice,
  ctaButton,
  showAtc,
  showPspCopy,
}) => {
  // 'showPspCopy' is used to determine if we should show the PSP copy or the PDP copy
  const descriptionCopy =
    showPspCopy && productDetails?.pspDetailsModalCopy
      ? productDetails?.pspDetailsModalCopy
      : productDetails?.pdpFlexContent || productDetails?.shortDescription;

  return (
    <section className={styles.products}>
      <>
        <h2 className={styles.name}>{productDetails?.name}</h2>
        <p className={styles.formFactor}>{productDetails?.formFactor}</p>

        <div className={carouselStyles.productModal}>
          <ProductBadge
            fullPrice={fullPrice}
            productDetails={productDetails}
            purchaseSku={purchaseSku}
            unitPrice={unitPrice}
          />
          <ProductCarousels images={images} videos={productDetails.videos} />
        </div>

        {showAtc && (
          <div className={styles.addToCart}>
            {ctaButton ? (
              ctaButton
            ) : (
              <AddProductForm
                afterSubmit={onClose}
                channel={channel}
                productDetails={productDetails}
                purchaseSku={purchaseSku}
                qtySelected={qtySelected}
                error={!purchaseSku}
                source={REGISTRATION_SOURCES.LAWN_FUNNEL}
              />
            )}
          </div>
        )}

        {productDetails?.tagLine && (
          <p className={styles.tagLine}>
            {productDetails.tagLine} <span className={styles.tagLineDash} />
          </p>
        )}
        <div className={styles.productDescription}>
          <BlockContent
            content={descriptionCopy}
            serializers={productContentSerializers}
          />
        </div>

        {showViewDetailsLink && (
          <ViewDetailsLink onClose={onClose} productDetails={productDetails} />
        )}
      </>
    </section>
  );
};

const ViewDetailsLink = ({ productDetails, onClose }) => {
  const navigate = useNavigate();

  const viewDetails = () => {
    onClose();
    navigate(
      sundayStoreProductDetailsRoute({
        category: productDetails?.categories?.[0]?.category?.slug,
        slug: productDetails?.slug,
      })
    );
  };

  return (
    <div className={styles.navButtonContainer}>
      <Button
        className={styles.navButton}
        onClick={() => viewDetails()}
        variant="link"
      >
        View full details
      </Button>
    </div>
  );
};
